import { Skeleton } from "@/components/ui/skeleton";
import { ServicePageProfile } from "@/utils/useServicePage";
import { BusinessProfile } from "./BusinessProfile";
import { BANNER_ASPECT_RATIO, RECOMMENDED_BANNER_WIDTH, RECOMMENDED_BANNER_HEIGHT } from "@/utils/imageUtils";
import { useIsMobile } from "@/hooks/use-mobile";
import { ServiceBannerSkeleton } from "./ServiceBannerSkeleton";
import { useAnalytics } from "@/hooks/useAnalytics";
import { useToast } from "@/components/ui/use-toast";
import { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';

interface ServiceBannerProps {
  profile: ServicePageProfile | null;
  isLoading: boolean;
}

export const ServiceBanner: React.FC<ServiceBannerProps> = ({
  profile,
  isLoading,
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { trackAction } = useAnalytics({ component: "ServiceBanner" });
  const { toast } = useToast();
  const [screenSize, setScreenSize] = useState<'small' | 'medium' | 'large'>(
    isMobile ? 'small' : 'large'
  );
  
  // Set up responsive breakpoints
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 480) {
        setScreenSize('small');
      } else if (window.innerWidth < 768) {
        setScreenSize('medium');
      } else {
        setScreenSize('large');
      }
    };
    
    // Initial check
    handleResize();
    
    // Add event listener
    window.addEventListener('resize', handleResize);
    
    // Cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  if (isLoading) {
    return <ServiceBannerSkeleton />;
  }

  if (!profile) return null;

  // Check if images exist
  const hasBannerImage = profile.banner_image_url && profile.banner_image_url.trim() !== '';
  const hasProfileImage = profile.profile_image_url && profile.profile_image_url.trim() !== '';
  
  // Calculate optimal banner height based on device
  const bannerHeight = isMobile 
    ? { height: '200px' }  // Shorter height on mobile
    : { height: `${RECOMMENDED_BANNER_HEIGHT}px` };

  const handleShare = () => {
    try {
      // Track the appropriate share action based on preference
      const shareType = profile.preferred_messaging === 'telegram' ? "telegram_share" : "whatsapp_share";
      trackAction(shareType, { userId: profile.id });
      
      const currentUrl = window.location.href;
      const message = `Check out this service page: ${currentUrl}`;
      const encodedMessage = encodeURIComponent(message);
      
      // Open the appropriate share URL
      const shareUrl = profile.preferred_messaging === 'telegram'
        ? `https://t.me/share/url?url=${encodedMessage}`
        : `https://wa.me/?text=${encodedMessage}`;
      
      window.open(shareUrl, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Error opening share:', error);
      toast({
        variant: "destructive",
        title: "Share Error",
        description: "Could not open sharing. Please try again.",
      });
    }
  };

  // Helper function for profile picture size
  const getProfilePictureSize = () => {
    // Keep the same size regardless of screen size
    return isMobile ? 'w-24 h-24' : 'w-32 h-32';
  };
  
  // Helper function for profile picture margin
  const getProfilePictureMargin = () => {
    switch (screenSize) {
      case 'small':
        return '-24px'; // Minimal overlap on very small screens
      case 'medium':
        return '-40px'; // Medium overlap on tablet
      case 'large':
      default:
        return '-80px'; // More overlap on desktops
    }
  };

  // If no images at all, show compact view
  if (!hasBannerImage && !hasProfileImage) {
    return (
      <div className="bg-white py-4 px-4">
        <div className="container mx-auto max-w-5xl">
          <div className="mt-2">
            <BusinessProfile
              businessName={profile.company_name || ""}
              profileImage="" 
              onShare={handleShare}
              firstName={profile.first_name}
              lastName={profile.last_name}
              profession={profile.profession}
              profileBio={profile.profile_bio}
              country={profile.country}
              userId={profile.id}
              preferredMessaging={(profile.preferred_messaging as 'whatsapp' | 'telegram' | null) || 'whatsapp'}
              hideProfileImage={true}
            />
          </div>
        </div>
      </div>
    );
  }

  // If only banner image exists
  if (hasBannerImage && !hasProfileImage) {
    return (
      <div className="relative w-full">
        <div className="w-full relative overflow-hidden rounded-lg">
          <div className="w-full" style={{ paddingTop: '30%' }}>
            <img
              loading="lazy"
              src={profile.banner_image_url}
              alt="Banner"
              className="absolute top-0 left-0 w-full h-full object-cover rounded-lg"
              onLoad={() => console.log('Banner image loaded successfully:', profile.banner_image_url)}
              onError={(e) => {
                console.error('Banner image failed to load:', {
                  url: profile.banner_image_url,
                  error: e
                });
                e.currentTarget.style.display = 'none';
              }}
            />
          </div>
        </div>
        <div className="container mx-auto max-w-5xl px-4">
          <div className="mt-4">
            <BusinessProfile
              businessName={profile.company_name || ""}
              profileImage="" 
              onShare={handleShare}
              firstName={profile.first_name}
              lastName={profile.last_name}
              profession={profile.profession}
              profileBio={profile.profile_bio}
              country={profile.country}
              userId={profile.id}
              preferredMessaging={(profile.preferred_messaging as 'whatsapp' | 'telegram' | null) || 'whatsapp'}
              hideProfileImage={true}
            />
          </div>
        </div>
      </div>
    );
  }

  // If only profile image exists
  if (!hasBannerImage && hasProfileImage) {
    return (
      <div className="bg-white py-6 px-4">
        <div className="container mx-auto max-w-5xl">
          <div className="flex flex-col items-center">
            <img
              loading="lazy"
              src={profile.profile_image_url}
              alt={profile.first_name || profile.last_name || "Profile"}
              className={`rounded-full border-4 border-white shadow-lg object-cover ${getProfilePictureSize()}`}
            />
            <div className="mt-4">
              <BusinessProfile
                businessName={profile.company_name || ""}
                profileImage="" 
                onShare={handleShare}
                firstName={profile.first_name}
                lastName={profile.last_name}
                profession={profile.profession}
                profileBio={profile.profile_bio}
                country={profile.country}
                userId={profile.id}
                preferredMessaging={(profile.preferred_messaging as 'whatsapp' | 'telegram' | null) || 'whatsapp'}
                hideProfileImage={true}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // If both images exist, show full layout
  return (
    <div className="relative w-full">
      <div className="w-full relative overflow-hidden rounded-lg">
        <div className="w-full" style={{ paddingTop: '30%' }}>
          <img
            loading="lazy"
            src={profile.banner_image_url}
            alt="Banner"
            className="absolute top-0 left-0 w-full h-full object-cover rounded-lg"
            onLoad={() => console.log('Banner image loaded successfully:', profile.banner_image_url)}
            onError={(e) => {
              console.error('Banner image failed to load:', {
                url: profile.banner_image_url,
                error: e
              });
              e.currentTarget.style.display = 'none';
            }}
          />
        </div>
      </div>
      
      <div className="container mx-auto max-w-5xl px-4">
        <div 
          className="flex justify-center relative z-10" 
          style={{ marginTop: getProfilePictureMargin() }}
        >
          <img
            loading="lazy"
            src={profile.profile_image_url}
            alt={profile.first_name || profile.last_name || "Profile"}
            className={`rounded-full border-4 border-white shadow-lg object-cover ${getProfilePictureSize()}`}
          />
        </div>
        
        <div className="mt-4">
          <BusinessProfile
            businessName={profile.company_name || ""}
            profileImage="" 
            onShare={handleShare}
            firstName={profile.first_name}
            lastName={profile.last_name}
            profession={profile.profession}
            profileBio={profile.profile_bio}
            country={profile.country}
            userId={profile.id}
            preferredMessaging={(profile.preferred_messaging as 'whatsapp' | 'telegram' | null) || 'whatsapp'}
            hideProfileImage={true}
          />
        </div>
      </div>
    </div>
  );
};

